import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AGE_OF_MAJORITY, DEFAULT_STANDING_ORDERS, INACTIVITY } from '@pk/shared';
import type { HouseView, Id, StandingOrders } from '@pk/shared';
import { actorOf, logout, useCommand, useHouse, useMe, useWorld } from '../api/hooks';
import { api, MOCK } from '../api/client';
import { useUi } from '../store';
import { Badge, Btn, Collapsible, ConfirmRow, ErrorBox, Field, Inline, Loading, PageHeader, Panel, Screen, ScreenTop, ScrollArea, Section, Segmented, Sheet } from '../components/ui';
import { IconAlert, IconCrown, IconGear, IconHourglass, IconLetter, IconShield, IconWar } from '../components/icons';
import { CharPortrait } from '../components/game';
import { registerPush } from '../lib/push';
import type { PushStatus } from '../lib/push';
import '../styles/screens-realm.css';

type Theme = 'dark' | 'light';

export function SettingsScreen() {
  const { t } = useTranslation();
  const house = useHouse();
  const me = useMe();
  if (house.isLoading) return <Loading />;
  if (house.isError || !house.data) return <ErrorBox error={house.error} onRetry={() => house.refetch()} />;
  const h = house.data;
  return (
    <Screen>
      <ScreenTop><PageHeader icon={<IconGear />} title={t('settings.title')} subtitle={t('settings.sub')} /></ScreenTop>
      <ScrollArea>
      <Section title={t('settings.groupHouse')}>
        <div className="settings-stack">
          <StandingOrdersSection house={h} />
          <Panel className="more">
            <HeadSection house={h} />
            <InactivitySection house={h} />
          </Panel>
        </div>
      </Section>
      <Section title={t('settings.groupApp')}>
        <div className="settings-stack">
          <Panel className="more">
            <AppCard />
            <PushCard />
            <AccountCard email={me.data?.email} premium={me.data?.premium} />
          </Panel>
        </div>
      </Section>
      </ScrollArea>
    </Screen>
  );
}

// ------------------------------------------------------------------ standing orders

function StandingOrdersSection({ house }: { house: HouseView }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const cur = house.standingOrders;
  const [draft, setDraft] = useState<StandingOrders>(cur);
  useEffect(() => { setDraft(cur); }, [cur.autoRaiseLevies, cur.callToArms, cur.retreatRatio, cur.marriageProposals, cur.rideWithLord]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = (Object.keys(draft) as (keyof StandingOrders)[]).some((k) => draft[k] !== cur[k]);
  const patch = <K extends keyof StandingOrders>(k: K, v: StandingOrders[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async () => {
    const orders: Partial<StandingOrders> = {};
    (Object.keys(draft) as (keyof StandingOrders)[]).forEach((k) => { if (draft[k] !== cur[k]) (orders as Record<string, unknown>)[k] = draft[k]; });
    await cmd.run({ type: 'set_standing_orders', actorId: actorOf(house), orders });
  };
  return (
    <Panel icon={<IconWar />} title={t('settings.orders.title')}>
      <p className="muted settings-help">{t('settings.orders.help')}</p>
      <div>
        <label className="switch-row">
          <span className="switch-text">
            <span className="switch-label">{t('settings.orders.autoRaise')}</span>
            <span className="small muted">{t('settings.orders.autoRaiseHint')}</span>
          </span>
          <input type="checkbox" role="switch" className="switch" checked={draft.autoRaiseLevies} onChange={(e) => patch('autoRaiseLevies', e.target.checked)} />
        </label>

        <label className="switch-row">
          <span className="switch-text">
            <span className="switch-label">{t('settings.orders.rideWithLord')}</span>
            <span className="small muted">{t('settings.orders.rideWithLordHint')}</span>
          </span>
          <input type="checkbox" role="switch" className="switch" checked={draft.rideWithLord ?? true} onChange={(e) => patch('rideWithLord', e.target.checked)} />
        </label>

        <div className="field-label">{t('settings.orders.callToArms')}</div>
        <Segmented<StandingOrders['callToArms']> value={draft.callToArms} onChange={(v) => patch('callToArms', v)} options={[
          { value: 'join', label: t('settings.orders.join') }, { value: 'refuse', label: t('settings.orders.refuse') },
        ]} />

        <Field label={draft.retreatRatio === 0 ? t('settings.orders.retreatNever') : t('settings.orders.retreatValue', { ratio: draft.retreatRatio.toFixed(2).replace(/\.?0+$/, '') })} hint={t('settings.orders.retreatHint')}>
          <input type="range" min={0} max={4} step={0.25} value={draft.retreatRatio} onChange={(e) => patch('retreatRatio', Number(e.target.value))} aria-label={t('settings.orders.retreat')} />
        </Field>

        <div className="field-label">{t('settings.orders.marriage')}</div>
        <Segmented<StandingOrders['marriageProposals']> value={draft.marriageProposals} onChange={(v) => patch('marriageProposals', v)} options={[
          { value: 'decline', label: t('settings.orders.decline') }, { value: 'accept_reasonable', label: t('settings.orders.acceptReasonable') },
        ]} />
        <p className="small muted">{t('settings.orders.marriageHint')}</p>

        <Inline error={cmd.error} />
        <div className="row settings-save">
          <Btn variant="primary" disabled={!dirty} busy={cmd.pending} onClick={() => void save()}>{t('common.save')}</Btn>
          <Btn variant="quiet" onClick={() => setDraft({ ...DEFAULT_STANDING_ORDERS })}>{t('settings.orders.reset')}</Btn>
        </div>
      </div>
    </Panel>
  );
}

// ------------------------------------------------------------------ head of house

function HeadSection({ house }: { house: HouseView }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const [pick, setPick] = useState<Id | null>(null);
  const head = house.members.find((m) => m.id === house.house.headId);
  const landless = !head || head.heldTitleIds.length === 0;
  const cands = house.members.filter((m) => m.alive && m.age >= AGE_OF_MAJORITY && !m.isHead && !m.prisonerOf);
  const chosen = cands.find((m) => m.id === pick);
  return (
    <Collapsible icon={<IconCrown />} title={t('settings.head.title')} summary={head ? head.firstName : t('common.none')}>
      <div className="kv"><span>{t('settings.head.current')}</span><span>{head ? head.firstName : t('common.none')}</span></div>
      {!landless && <p className="small muted">{t('settings.head.landed')}</p>}
      {landless && cands.length === 0 && <p className="small muted">{t('settings.head.none')}</p>}
      {landless && cands.length > 0 && (
        <>
          <p className="small muted">{t('settings.head.help')}</p>
          <ul className="picklist plainlist">
            {cands.map((m) => (
              <li key={m.id}>
                <button type="button" className="pick" onClick={() => setPick(m.id)}>
                  <CharPortrait c={m} size={30} /><span>{m.firstName}</span>
                  <span className="muted small">{t('common.age', { age: m.age })}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <Sheet open={!!chosen} onClose={() => { setPick(null); cmd.clearError(); }} title={t('character.act.head')}>
        {chosen && (
          <>
            <ConfirmRow message={t('head.confirm', { name: chosen.firstName })} busy={cmd.pending}
              onConfirm={async () => { const r = await cmd.run({ type: 'set_head', actorId: actorOf(house), newHeadId: chosen.id }); if (r.ok) setPick(null); }}
              onCancel={() => setPick(null)} />
            <Inline error={cmd.error} />
          </>
        )}
      </Sheet>
    </Collapsible>
  );
}

// ------------------------------------------------------------------ inactivity

function InactivitySection({ house }: { house: HouseView }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const world = useWorld();
  const stage = house.inactivity.stage;
  const rank = world.data?.houses.find((x) => x.id === house.house.id)?.highestRank ?? 'landless';
  const th = INACTIVITY[rank];
  const days = Math.floor(house.inactivity.realDaysInactive);
  const stages = [
    { key: 'active', text: t('settings.inactivity.active') },
    { key: 'caretaker', text: t('settings.inactivity.caretaker', { days: th.caretakerDays }) },
    { key: 'deposable', text: th.deposableDays === null ? t('settings.inactivity.deposableNever') : t('settings.inactivity.deposable', { days: th.deposableDays }) },
    { key: 'game_over', text: t('settings.inactivity.gameOver', { days: th.gameOverDays }) },
  ];
  return (
    <Collapsible icon={<IconHourglass />} title={t('settings.inactivity.title')} defaultOpen={stage !== 'active'}
      summary={<span className={stage === 'active' ? '' : 'neg'}>{t('settings.inactivity.away', { days })}</span>}>
      <div>
        <h4 className="settings-stage">{t(`inactivity.${stage}.title`)}</h4>
        <p>{t(`inactivity.${stage}.text`, { days })}</p>
        <ol className="stages">
          {stages.map((s) => <li key={s.key} className={s.key === stage ? 'now' : ''}>{s.text}</li>)}
        </ol>
        <p className="small muted">{t('settings.inactivity.rankNote', { rank: rank === 'landless' ? t('common.landless') : t(`rank.${rank}`) })}</p>
        {stage !== 'active' && (
          <Btn small variant="primary" onClick={() => { void api.heartbeat().then(() => qc.invalidateQueries({ queryKey: ['house'] })); }}>{t('inactivity.resume')}</Btn>
        )}
      </div>
    </Collapsible>
  );
}

// ------------------------------------------------------------------ account & app

function AccountCard({ email, premium }: { email?: string; premium?: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  return (
    <Collapsible icon={<IconShield />} title={t('settings.account')} summary={email ?? '-'}>
      <div className="kv"><span>{t('settings.email')}</span><span style={{ overflowWrap: 'anywhere', textAlign: 'right' }}>{email ?? '-'}</span></div>
      {premium && <div className="kv"><span>{t('settings.premium')}</span><Badge tone="gold">{t('settings.premiumOn')}</Badge></div>}
      {confirm ? (
        <ConfirmRow message={t('settings.logoutConfirm')} onConfirm={() => void logout(qc)} onCancel={() => setConfirm(false)} />
      ) : (
        <div style={{ marginTop: 14 }}><Btn variant="quiet" onClick={() => setConfirm(true)}>{t('settings.logout')}</Btn></div>
      )}
    </Collapsible>
  );
}

function AppCard() {
  const { t, i18n } = useTranslation();
  const wsConnected = useUi((s) => s.wsConnected);
  const [theme, setThemeState] = useState<Theme>(() => {
    try { const v = localStorage.getItem('pk.theme'); return v === 'light' ? 'light' : 'dark'; } catch { return 'dark'; }
  });
  const setTheme = (v: Theme) => {
    setThemeState(v);
    try { localStorage.setItem('pk.theme', v); } catch { /* ignore */ }
    document.documentElement.dataset.theme = v;
  };
  return (
    <Collapsible icon={<IconGear />} title={t('settings.app')} summary={theme === 'dark' ? t('settings.themeDark') : t('settings.themeLight')} defaultOpen>
      <Field label={t('settings.language')} hint={t('settings.languageHint')}>
        <select className="input" value={i18n.language.slice(0, 2)} onChange={(e) => void i18n.changeLanguage(e.target.value)}>
          <option value="en">English</option>
        </select>
      </Field>
      <div className="field-label">{t('settings.theme')}</div>
      <Segmented<Theme> value={theme} onChange={setTheme} options={[
        { value: 'dark', label: t('settings.themeDark') }, { value: 'light', label: t('settings.themeLight') },
      ]} />
      <div className="kv"><span>{t('settings.connection')}</span>
        <span>{MOCK ? <Badge tone="blue">{t('settings.mock')}</Badge> : wsConnected ? <Badge tone="green">{t('settings.live')}</Badge> : <Badge>{t('settings.polling')}</Badge>}</span>
      </div>
    </Collapsible>
  );
}

// ------------------------------------------------------------------ push notifications

/** Neutral statuses: show plain text, never red/"Blocked" (permission is simply not granted yet). */
const NEUTRAL_STATUSES: ReadonlySet<PushStatus> = new Set(['prompt', 'unconfigured']);

function PushCard() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Silent status check: never triggers a permission prompt.
    void registerPush({ requestPermission: false }).then((r) => {
      setStatus(r.status);
      setDetail(r.detail ?? null);
    });
  }, []);

  const enable = () => {
    setBusy(true);
    void registerPush({ requestPermission: true })
      .then((r) => { setStatus(r.status); setDetail(r.detail ?? null); })
      .catch(() => { setStatus('error'); setDetail(t('settings.push.status.error')); })
      .finally(() => setBusy(false));
  };

  return (
    <Collapsible icon={<IconLetter />} title={t('settings.push.title')}
      summary={status === 'enabled' ? t('settings.push.on') : status === 'denied' ? t('settings.push.blocked') : status ? t(`settings.push.status.${status}`) : '…'}>
      <div className="kv push-kv">
        <span>{t('settings.push.alerts')}</span>
        <span aria-live="polite" role="status">
          {status === 'enabled' ? <Badge tone="green">{t('settings.push.on')}</Badge>
            : status === 'denied' ? <Badge tone="red">{t('settings.push.blocked')}</Badge>
            : status ? <Badge tone={NEUTRAL_STATUSES.has(status) ? 'blue' : 'plain'}>{t(`settings.push.status.${status}`)}</Badge>
            : <Badge>…</Badge>}
        </span>
      </div>
      <p className="small muted">{t('settings.push.help')}</p>
      {detail && status !== 'enabled' && (
        <p className="small muted" aria-live="polite">{status === 'prompt' ? t('settings.push.notEnabled', { detail }) : detail}</p>
      )}
      {(status === null || status === 'prompt' || status === 'error') && (
        <div style={{ marginTop: 10 }}>
          <Btn variant="primary" small busy={busy} onClick={enable}>{t('settings.push.enable')}</Btn>
        </div>
      )}
    </Collapsible>
  );
}
